import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';
import { format } from 'date-fns';
import {
  Activity,
  Loader2,
  RadioTower,
  ChevronRight,
  ChevronDown,
} from 'lucide-react';

import { PageHeader } from '@/components/layout/page-header';
import { Pagination } from '@/components/data-table/pagination';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

import { useSessions, useSession, useInvalidateSessions, type SessionRow } from '@/hooks/use-sessions';
import { useEventStream } from '@/hooks/use-event-stream';
import { toast } from '@/hooks/use-toast';
import { absoluteTime, relativeTime, shortId, formatBytes } from '@/lib/utils';
import type { SessionStatus } from '@weremote/shared';
import { ExportCsvButton } from '@/components/export-csv-button';

export const Route = createFileRoute('/_app/sessions')({
  component: SessionsPage,
});

function SessionsPage() {
  const [page, setPage] = React.useState(1);
  const [status, setStatus] = React.useState<SessionStatus | 'all'>('all');
  const [fromDate, setFromDate] = React.useState('');
  const [toDate, setToDate] = React.useState('');
  const [expanded, setExpanded] = React.useState<string | null>(null);

  const [liveStatus, setLiveStatus] = React.useState<'connecting' | 'connected' | 'idle'>('connecting');
  const lastEventAtRef = React.useRef<number>(0);

  const invalidate = useInvalidateSessions();

  useEventStream({
    path: '/sessions/stream',
    onEvent: (event, data) => {
      lastEventAtRef.current = Date.now();
      setLiveStatus('connected');

      if (event === 'session.start' || event === 'session.end') {
        invalidate();
      }
      if (event === 'session.start') {
        const sid = (data as { data?: { sessionId?: string } })?.data?.sessionId ?? '?';
        toast({
          title: 'New session',
          description: `Session ${shortId(sid)} started.`,
        });
      }
    },
  });

  React.useEffect(() => {
    const t = setInterval(() => {
      const age = Date.now() - lastEventAtRef.current;
      if (lastEventAtRef.current === 0) return;
      if (age > 40_000) {
        setLiveStatus('idle');
      } else {
        setLiveStatus('connected');
      }
    }, 5000);
    return () => clearInterval(t);
  }, []);

  const { data, isLoading } = useSessions({
    page,
    pageSize: 25,
    status: status === 'all' ? undefined : status,
    fromDate: fromDate ? new Date(fromDate).toISOString() : undefined,
    toDate: toDate ? new Date(toDate).toISOString() : undefined,
  });

  return (
    <>
      <PageHeader
        title="Sessions"
        description="Live and historical remote-support sessions across the organization."
        actions={
          <>
            <ExportCsvButton
              domain="sessions"
              query={{
                status: status === 'all' ? undefined : status,
                fromDate: fromDate ? new Date(fromDate).toISOString() : undefined,
                toDate: toDate ? new Date(toDate).toISOString() : undefined,
              }}
            />
            <LiveBadge status={liveStatus} />
          </>
        }
      />

      <Card className="mb-4 p-3">
        <div className="grid gap-3 sm:grid-cols-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Status</Label>
            <Select value={status} onValueChange={(v: SessionStatus | 'all') => { setStatus(v); setPage(1); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="ENDED">Ended</SelectItem>
                <SelectItem value="FAILED">Failed</SelectItem>
                <SelectItem value="REJECTED">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2 grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">From</Label>
              <Input type="datetime-local" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setPage(1); }} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">To</Label>
              <Input type="datetime-local" value={toDate} onChange={(e) => { setToDate(e.target.value); setPage(1); }} />
            </div>
          </div>
          <div className="flex flex-col justify-end">
            {(status !== 'all' || fromDate || toDate) && (
              <Button variant="ghost" size="sm" onClick={() => { setStatus('all'); setFromDate(''); setToDate(''); setPage(1); }}>
                Reset filters
              </Button>
            )}
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-6" />
              <TableHead>Status</TableHead>
              <TableHead>Device</TableHead>
              <TableHead>Technician</TableHead>
              <TableHead>Started</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>ID</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={7} className="h-32 text-center text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></TableCell></TableRow>
            ) : data?.data.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="h-32 text-center text-muted-foreground">
                <Activity className="mx-auto h-5 w-5 mb-2 opacity-50" />
                No sessions match the current filters.
              </TableCell></TableRow>
            ) : (
              data?.data.map((s) => (
                <SessionRowDisplay
                  key={s.id}
                  session={s}
                  expanded={expanded === s.id}
                  onToggle={() => setExpanded(expanded === s.id ? null : s.id)}
                />
              ))
            )}
          </TableBody>
        </Table>
        {data && (
          <div className="border-t">
            <Pagination page={page} pageSize={data.pageSize} total={data.total} onPageChange={setPage} />
          </div>
        )}
      </Card>
    </>
  );
}

function LiveBadge({ status }: { status: 'connecting' | 'connected' | 'idle' }) {
  if (status === 'connecting') {
    return (
      <span className="inline-flex items-center gap-1.5 text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        <span className="font-mono text-2xs uppercase tracking-widest">Connecting…</span>
      </span>
    );
  }
  if (status === 'idle') {
    return (
      <span className="inline-flex items-center gap-1.5 text-muted-foreground">
        <span className="status-dot status-dot--warning" />
        <span className="font-mono text-2xs uppercase tracking-widest">Reconnecting</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-success">
      <RadioTower className="h-3.5 w-3.5 animate-pulse-dot" />
      <span className="font-mono text-2xs uppercase tracking-widest">Live</span>
    </span>
  );
}

function SessionRowDisplay({ session, expanded, onToggle }: { session: SessionRow; expanded: boolean; onToggle: () => void }) {
  return (
    <>
      <TableRow className="cursor-pointer" onClick={onToggle}>
        <TableCell>
          {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        </TableCell>
        <TableCell><StatusBadge status={session.status} /></TableCell>
        <TableCell>
          <div className="flex flex-col">
            <span className="text-sm font-medium">
              {session.device.alias || session.device.hostname || '—'}
            </span>
            <span className="id-pill mt-0.5">{session.device.rustdeskId}</span>
          </div>
        </TableCell>
        <TableCell>
          {session.technician ? (
            <div className="flex flex-col">
              <span className="text-sm">
                {session.technician.firstName || session.technician.lastName
                  ? `${session.technician.firstName ?? ''} ${session.technician.lastName ?? ''}`.trim()
                  : session.technician.email}
              </span>
              <span className="text-2xs text-muted-foreground">{session.technician.email}</span>
            </div>
          ) : (
            <span className="text-muted-foreground text-sm">—</span>
          )}
        </TableCell>
        <TableCell className="text-sm text-muted-foreground tabular-nums">{relativeTime(session.startedAt)}</TableCell>
        <TableCell className="font-mono text-xs tabular-nums">
          {session.status === 'ACTIVE' ? (
            <LiveDuration startedAt={session.startedAt} />
          ) : session.durationSec !== null ? (
            formatDuration(session.durationSec)
          ) : (
            '—'
          )}
        </TableCell>
        <TableCell><span className="id-pill">{shortId(session.id)}</span></TableCell>
      </TableRow>
      {expanded && <SessionDetailRow session={session} />}
    </>
  );
}

function StatusBadge({ status }: { status: SessionStatus }) {
  const variant: 'success' | 'secondary' | 'destructive' | 'warning' =
    status === 'ACTIVE' ? 'success' : status === 'ENDED' ? 'secondary' : status === 'REJECTED' ? 'warning' : 'destructive';
  return (
    <span className="inline-flex items-center gap-1.5">
      {status === 'ACTIVE' && <span className="status-dot status-dot--online" />}
      <Badge variant={variant}>{status.charAt(0) + status.slice(1).toLowerCase()}</Badge>
    </span>
  );
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function LiveDuration({ startedAt }: { startedAt: string }) {
  const [, setTick] = React.useState(0);
  React.useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const sec = Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000));
  return <span className="text-primary">{formatDuration(sec)}</span>;
}

function SessionDetailRow({ session }: { session: SessionRow }) {
  const detail = useSession(session.id);

  return (
    <TableRow>
      <TableCell colSpan={7} className="bg-muted/30 py-4">
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="space-y-1 font-mono text-xs">
            <Field label="Session ID" value={session.id} />
            <Field label="Started" value={absoluteTime(session.startedAt)} />
            <Field label="Ended" value={absoluteTime(session.endedAt)} />
            <Field label="End reason" value={session.endReason ?? '—'} />
          </div>
          <div className="space-y-1 font-mono text-xs">
            <Field label="Relay" value={session.relayUsed ? 'Yes' : 'No'} />
            <Field label="Technician IP" value={session.technicianIp ?? '—'} />
            <Field label="Bytes in" value={formatBytes(session.bytesIn)} />
            <Field label="Bytes out" value={formatBytes(session.bytesOut)} />
          </div>
          <div className="space-y-1">
            <p className="text-2xs font-mono uppercase tracking-wider text-muted-foreground mb-2">
              Events ({detail.data?.events.length ?? 0})
            </p>
            {detail.isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            ) : (detail.data?.events ?? []).length === 0 ? (
              <p className="text-xs text-muted-foreground italic">No granular events recorded.</p>
            ) : (
              <div className="max-h-40 overflow-y-auto space-y-1">
                {detail.data?.events.map((ev) => (
                  <div key={ev.id} className="flex gap-2 text-2xs font-mono">
                    <span className="text-muted-foreground tabular-nums shrink-0">
                      {format(new Date(ev.timestamp), 'HH:mm:ss')}
                    </span>
                    <span className="text-primary">{ev.type}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </TableCell>
    </TableRow>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-2">
      <span className="text-muted-foreground shrink-0 w-24">{label}</span>
      <span className="truncate">{value}</span>
    </div>
  );
}
